import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { type PaymentTransactionsInput, requestPaymentTransactions } from './transactionsApi';

/**
 * Page-scoped so each offset/limit combination caches independently. Exported so a future receipt
 * or refund mutation can invalidate the whole `['payments', 'transactions']` prefix at once.
 */
export const paymentTransactionsKey = ({ offset, limit }: PaymentTransactionsInput) =>
  ['payments', 'transactions', { offset, limit }] as const;

/**
 * One page of the member's payment history. Callers own the paging maths: the Past Transactions
 * page requests `{ offset: (page - 1) * 10, limit: 10 }`, while the Payment Center's Recent
 * Transactions widget requests `{ offset: 0, limit: 3 }`.
 *
 * `keepPreviousData` holds the previous page on screen while the next one loads, so paging swaps
 * rows in place instead of flashing the skeleton state between pages.
 */
export function usePaymentTransactions(input: PaymentTransactionsInput) {
  return useQuery({
    queryKey: paymentTransactionsKey(input),
    queryFn: () => requestPaymentTransactions(input),
    placeholderData: keepPreviousData,
  });
}
